import { HttpMethods, makeRequest } from '../helpers/http.helper';

export type WasteCatalogOption = { value: string; label: string; category?: string };

type CatalogMaterial = { id: string; name: string; selectable?: boolean; status?: string };
type CatalogCategory = { code: string; name: string; materials?: CatalogMaterial[] };

type CatalogParams = {
  context: string;
  organization_id?: string;
  include_all_materials?: boolean;
  include_inactive?: boolean;
};

async function fetchCatalog(params: CatalogParams) {
  const response = await makeRequest({
    url: '/waste-catalog/options',
    method: HttpMethods.GET,
    params,
  });
  return response?.result ?? response;
}

function toMaterialOptions(
  result: { categories?: CatalogCategory[] },
  opts: { includeAll?: boolean } = {},
): WasteCatalogOption[] {
  // The category rides along so pickers can group by it, the way the catalog itself
  // is organised -- a flat list of every waste type is hard to scan.
  return ((result?.categories ?? []) as CatalogCategory[])
    .flatMap((category) => (category.materials ?? []).map((material) => ({ material, category })))
    .filter(({ material }) => opts.includeAll || (material.selectable !== false && material.status !== 'deprecated'))
    .map(({ material, category }) => ({ value: material.id, label: material.name, category: category.name }));
}

export async function getWasteCatalogCategoryOptions(organizationId?: string): Promise<WasteCatalogOption[]> {
  const result = await fetchCatalog({ context: 'ops', organization_id: organizationId });
  return (result?.categories ?? []).map((category: { code: string; name: string }) => ({
    value: category.code,
    label: category.name,
  }));
}

/**
 * The waste types this organization handles — what its pickers should offer.
 *
 * Deliberately a separate function from `getAllWasteCatalogMaterials` rather than
 * one function with a flag: getting this wrong fails silently, and only on
 * historical data, so each caller is made to say which of the two it wants.
 */
export async function getWasteCatalogMaterialOptions(organizationId?: string): Promise<WasteCatalogOption[]> {
  const result = await fetchCatalog({ context: 'ops', organization_id: organizationId });
  return toMaterialOptions(result);
}

/**
 * Every waste type in the catalog, whoever handles it.
 *
 * For resolving a name to display — never for a picker. A record can reference a
 * waste type its organization has since stopped handling, and it must still show
 * that name rather than a raw id. `include_inactive` is what actually makes the
 * backend include deactivated/non-selectable materials (the route ignores
 * `include_all_materials` for that -- it only widens the organization-basket
 * scoping); `includeAll` mirrors that by skipping this file's own client-side
 * selectable/deprecated filter, which would otherwise drop them right back out.
 */
/**
 * Every ACTIVE, selectable waste type in the catalog -- for pickers that describe what
 * some OTHER party produces. A supplier's accepted waste types are the supplier's,
 * not the recycler's: scoping this picker to the owner organization's own basket hid
 * most of the catalog from recyclers and waste movers.
 */
export async function getFullWasteCatalogMaterialOptions(): Promise<WasteCatalogOption[]> {
  const result = await fetchCatalog({ context: 'ops' });
  return toMaterialOptions(result);
}

export async function getAllWasteCatalogMaterials(): Promise<WasteCatalogOption[]> {
  const result = await fetchCatalog({ context: 'ops', include_all_materials: true, include_inactive: true });
  return toMaterialOptions(result, { includeAll: true });
}
